import { type ComponentProps, forwardRef, useCallback, useRef } from 'react';
import { LinearGradient } from 'expo-linear-gradient';
import {
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  type ScrollViewInstance,
  StyleSheet,
  View,
} from 'react-native';
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';

import { useA11ySelector } from '@sg/a11y-helpers';
import { Scrollspy, theme, useContainerSpacing, useResponsive } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const MenuCategoriesGridNav = forwardRef<ScrollViewInstance, MenuCategoriesGridNavProps>(
  (props, ref) => {
    const { children, onScroll, ...restProps } = props;

    const a11ySelector = useA11ySelector('menu.nav');

    const { currentBreakpoint } = useResponsive();
    const containerSpacing = useContainerSpacing();
    const { right: safeAreaRightInset } = useSafeAreaInsets();
    const gradientOpacitySV = useSharedValue(1);

    // ─── Refs ────────────────────────────────────────────────────────────────

    const hasReachedEndRef = useRef(false);

    // ─── Derived ─────────────────────────────────────────────────────────────

    const shouldApplyRightInset = currentBreakpoint.isXS && safeAreaRightInset > 0;

    // ─── Handlers ────────────────────────────────────────────────────────────

    const updateGradientOpacity = useCallback(
      (event: NativeSyntheticEvent<NativeScrollEvent>) => {
        const { contentOffset, contentSize, layoutMeasurement } = event.nativeEvent;

        const remainingOffset = contentSize.width - layoutMeasurement.width - contentOffset.x;
        const hasReachedEnd = remainingOffset <= SCROLL_THRESHOLD;

        if (hasReachedEnd === hasReachedEndRef.current) {
          return;
        }

        hasReachedEndRef.current = hasReachedEnd;
        gradientOpacitySV.set(withTiming(hasReachedEnd ? 0 : 1, GRADIENT_TIMING));
      },
      [gradientOpacitySV],
    );

    const handleScroll = useCallback(
      (event: NativeSyntheticEvent<NativeScrollEvent>) => {
        updateGradientOpacity(event);
        onScroll?.(event);
      },
      [onScroll, updateGradientOpacity],
    );

    // ─── Styles ──────────────────────────────────────────────────────────────

    const containerDynamicStyle = useStyle(
      () => ({ marginRight: shouldApplyRightInset ? safeAreaRightInset : 0 }),
      [shouldApplyRightInset, safeAreaRightInset],
    );

    const gradientAnimatedStyle = useAnimatedStyle(() => ({
      opacity: gradientOpacitySV.value,
    }));

    const gradientStyles = [styles.gradient, gradientAnimatedStyle];

    // ─────────────────────────────────────────────────────────────────────────

    return (
      <View style={containerDynamicStyle}>
        <Scrollspy.Nav
          ref={ref}
          stagePadding={currentBreakpoint.isLG ? undefined : containerSpacing}
          onScroll={handleScroll}
          {...a11ySelector.props}
          {...restProps}
        >
          {children}
        </Scrollspy.Nav>

        {shouldApplyRightInset ? (
          <Animated.View pointerEvents="none" style={gradientStyles}>
            <LinearGradient
              colors={GRADIENT_COLORS}
              end={GRADIENT_END}
              start={GRADIENT_START}
              style={styles.gradientFill}
            />
          </Animated.View>
        ) : null}
      </View>
    );
  },
);

MenuCategoriesGridNav.displayName = 'MenuCategoriesGridNav';

// ─── Constants ───────────────────────────────────────────────────────────────

const GRADIENT_COLORS = [
  theme.colors.OPACITY.CREAM.TRANSPARENT,
  theme.colors.APP_BACKGROUND,
] as const;
const GRADIENT_START = { x: 0, y: 0 } as const;
const GRADIENT_END = { x: 1, y: 0 } as const;
const GRADIENT_TIMING = { duration: theme.transitions.base } as const;

const SCROLL_THRESHOLD = 4;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  gradient: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    width: theme.spacing['12'],
  },
  gradientFill: {
    flex: 1,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type MenuCategoriesGridNavProps = ComponentProps<typeof Scrollspy.Nav>;
